import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { useSubscription } from '../context/SubscriptionContext';
import { formatCurrency } from '../utils/helpers';
import { motion, AnimatePresence } from 'framer-motion';

const PartnerPortal = () => {
  const navigate = useNavigate();
  const { partners, portalResources } = useSubscription();
  const [copied, setCopied] = useState(false);
  
  // NYTT: State for å bytte fane
  const [activeTab, setActiveTab] = useState('oversikt');

  const partner = partners?.[0] || { id: 'demo', name: 'Demo Partner AS', customers: [], commissionRate: 0.2 };
  const referralLink = `https://saleswork.no/register?ref=${partner.id}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const calculateTotalMRR = () => {
    return (partner.customers || []).reduce((sum, c) => sum + (c.mrr || 0), 0);
  };

  const estimatedCommission = calculateTotalMRR() * partner.commissionRate;

  // Filtrer ressurser for partner
  const myResources = (portalResources || []).filter(r => r.audience === 'partner' || r.audience === 'all');

  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      <header className="bg-white border-b border-slate-200 px-8 py-4 flex justify-between items-center sticky top-0 z-50">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center text-white shadow-md">
            <SafeIcon icon="💼" className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-sm font-black text-slate-900 uppercase tracking-widest">Partner Dashboard</h1>
            <p className="text-xs text-slate-500 font-bold">{partner.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <button className="text-sm font-bold text-slate-600 hover:text-slate-900 flex items-center gap-2">
            <SafeIcon icon="🏠" className="w-4 h-4" /> Hjem
          </button>
          <button onClick={() => navigate('/login')} className="text-sm font-bold text-rose-600 hover:text-rose-700 bg-rose-50 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors">
            <SafeIcon icon="⬅️" className="w-4 h-4" /> Logg ut
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 space-y-8">
        <div className="bg-[#3b5bdb] rounded-[2rem] p-8 text-white shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-[80px] -mr-20 -mt-20 pointer-events-none"></div>
          <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-4">
                <h2 className="text-3xl font-black tracking-tight">Din Verve-lenke</h2>
                <span className="px-3 py-1 bg-emerald-500/20 text-emerald-100 border border-emerald-500/30 rounded-full text-[10px] font-black uppercase tracking-widest">Aktiv</span>
              </div>
              <p className="text-blue-100 font-medium mb-6 max-w-lg">
                Del denne lenken med dine kunder for å registrere provisjon automatisk når de oppretter en konto.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1 bg-black/20 border border-white/20 rounded-xl p-1 flex items-center">
                  <input readOnly value={referralLink} className="flex-1 bg-transparent text-sm font-mono px-4 text-white outline-none" />
                  <button onClick={handleCopy} className="px-6 py-3 bg-white text-[#3b5bdb] rounded-lg text-xs font-black uppercase tracking-widest hover:bg-blue-50 transition-all flex items-center gap-2">
                    <SafeIcon icon={copied ? "✅" : "📋"} /> {copied ? 'Kopiert' : 'Kopier'}
                  </button>
                </div>
              </div>
            </div>
            <div className="bg-white/10 border border-white/20 p-6 rounded-2xl backdrop-blur-md text-center min-w-[200px]">
              <p className="text-[10px] text-blue-200 font-black uppercase tracking-widest mb-1">Partner ID</p>
              <p className="text-2xl font-black tracking-wider">{partner.id}</p>
              <div className="mt-4 inline-flex items-center gap-1 text-[10px] text-emerald-300 font-bold uppercase tracking-widest">
                <SafeIcon icon="✓" className="w-3 h-3" /> Verifisert
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 bg-white p-2 rounded-2xl shadow-sm border border-slate-200 w-fit">
          <button onClick={() => setActiveTab('oversikt')} className={`px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest flex items-center gap-2 transition-all ${activeTab === 'oversikt' ? 'bg-[#3b5bdb] text-white shadow-md' : 'text-slate-500 hover:bg-slate-50'}`}>
            <SafeIcon icon="📈" /> Oversikt & Salg
          </button>
          <button onClick={() => setActiveTab('ressurser')} className={`px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest flex items-center gap-2 transition-all ${activeTab === 'ressurser' ? 'bg-[#3b5bdb] text-white shadow-md' : 'text-slate-500 hover:bg-slate-50'}`}>
            <SafeIcon icon="📚" /> Ressurssenter
          </button>
          <button onClick={() => setActiveTab('meldinger')} className={`px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest flex items-center gap-2 transition-all ${activeTab === 'meldinger' ? 'bg-[#3b5bdb] text-white shadow-md' : 'text-slate-500 hover:bg-slate-50'}`}>
            <SafeIcon icon="💬" /> Meldinger
          </button>
        </div>

        <AnimatePresence mode="wait">
          {activeTab === 'oversikt' && (
            <motion.div key="oversikt" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm flex items-center gap-6">
                  <div className="w-16 h-16 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center text-2xl">
                    <SafeIcon icon="👥" />
                  </div>
                  <div>
                    <p className="text-xs font-black text-slate-400 uppercase tracking-widest">Aktive Kunder</p>
                    <p className="text-4xl font-black text-slate-900 mt-1">{(partner.customers || []).length}</p>
                  </div>
                </div>
                <div className="bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm flex items-center gap-6">
                  <div className="w-16 h-16 bg-purple-50 text-purple-600 rounded-2xl flex items-center justify-center text-2xl">
                    <SafeIcon icon="💰" />
                  </div>
                  <div>
                    <p className="text-xs font-black text-slate-400 uppercase tracking-widest">Estimert Provisjon ({(partner.commissionRate || 0) * 100}%)</p>
                    <p className="text-4xl font-black text-slate-900 mt-1">{formatCurrency(estimatedCommission)} <span className="text-sm text-slate-500">/mnd</span></p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-[2.5rem] border border-slate-200 shadow-sm p-8">
                <h3 className="text-lg font-black text-slate-900 mb-6">Kundeportefølje</h3>
                {(partner.customers || []).length > 0 ? (
                  <table className="w-full text-left">
                    <thead className="border-b border-slate-100">
                      <tr>
                        <th className="pb-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Kunde</th>
                        <th className="pb-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Plan</th>
                        <th className="pb-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Registrert</th>
                        <th className="pb-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">MRR</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {partner.customers.map((c, i) => (
                        <tr key={i} className="hover:bg-slate-50 transition-colors">
                          <td className="py-4 font-bold text-slate-900">{c.name}</td>
                          <td className="py-4"><span className="px-2 py-1 bg-slate-100 text-slate-600 text-[10px] font-bold uppercase rounded">{c.plan}</span></td>
                          <td className="py-4 text-sm text-slate-500">{c.signupDate}</td>
                          <td className="py-4 text-right font-black text-emerald-600">{formatCurrency(c.mrr)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center py-16">
                    <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
                      <SafeIcon icon="👥" className="text-3xl text-slate-300" />
                    </div>
                    <p className="text-sm font-bold text-slate-500 uppercase tracking-widest">Ingen kunder i porteføljen ennå</p>
                    <p className="text-xs text-slate-400 mt-2">Bruk verve-lenken over for å komme i gang.</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {activeTab === 'ressurser' && (
            <motion.div key="ressurser" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="bg-white rounded-[2.5rem] border border-slate-200 shadow-sm p-8 min-h-[400px]">
              <h3 className="text-xl font-black text-slate-900 mb-6 flex items-center gap-2">
                <SafeIcon icon="📚" className="text-blue-600" /> Partner Ressurser
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {myResources.length > 0 ? myResources.map(res => (
                  <div key={res.id} className="p-6 bg-slate-50 border border-slate-200 rounded-2xl hover:border-blue-300 hover:shadow-md transition-all cursor-pointer group">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl mb-4 ${res.type === 'PDF' ? 'bg-red-100 text-red-600' : res.type === 'Video' ? 'bg-purple-100 text-purple-600' : 'bg-blue-100 text-blue-600'}`}>
                      <SafeIcon icon={res.type === 'PDF' ? "📄" : res.type === 'Video' ? "🎥" : "📝"} />
                    </div>
                    <h4 className="font-bold text-slate-900 text-lg group-hover:text-blue-600 transition-colors">{res.title}</h4>
                    <p className="text-sm text-slate-500 mt-2 line-clamp-2">{res.description}</p>
                    <div className="mt-4 pt-4 border-t border-slate-200 flex justify-between items-center text-xs font-bold text-slate-400">
                      <span>{res.type}</span>
                      <SafeIcon icon="➡️" className="group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                )) : (
                  <div className="col-span-full py-12 text-center text-slate-400 border-2 border-dashed border-slate-100 rounded-2xl">
                    <SafeIcon icon="🤷" className="text-4xl mb-3 opacity-50" />
                    <p>Ingen ressurser er publisert av administrator enda.</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {activeTab === 'meldinger' && (
            <motion.div key="meldinger" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="bg-white rounded-[2.5rem] border border-slate-200 shadow-sm p-12 text-center min-h-[400px] flex flex-col items-center justify-center">
              <SafeIcon icon="💬" className="text-6xl text-slate-200 mb-4" />
              <h3 className="text-2xl font-black text-slate-900 mb-2">Innboks</h3>
              <p className="text-slate-500 max-w-md mx-auto mb-6">Her kan du kommunisere direkte med din partner manager.</p>
              <button className="px-6 py-3 bg-slate-900 text-white rounded-xl font-bold">Start ny samtale</button>
            </motion.div>
          )}
        </AnimatePresence>

      </main>
    </div>
  );
};

export default PartnerPortal;